import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useQuery } from './useQuery';
import type { Purchase, PurchaseLine, PurchaseStatus } from '../types';
import type { ExtractedBL } from '../lib/pdfExtractor';
import type { CatalogFeedResult } from '../lib/catalogFeedingService';
import { getFirstDayOfMonth, getToday } from '../utils/dateUtils';

export interface Supplier { id: string; name: string; contact_name?: string; phone?: string; category?: string }
export interface Project { id: string; code: string; name: string; is_active: boolean }

export interface ExtractionResult {
  file: File;
  result: ExtractedBL | null;
  error: string | null;
  _uploadResult?: { filePath: string; publicUrl: string } | null;
  _resolvedSupplierId?: string | null;
}

// Nouveau BL vide
export const emptyLine = (): Partial<PurchaseLine> => ({
  reference: '',
  designation: '',
  quantity: 1,
  unit: 'pce',
  unit_price: 0
});

export function usePurchasesData() {
  // Onglet actif
  const [activeTab, setActiveTab] = useState<'bl' | 'suppliers'>('bl');

  // Formulaire fournisseur
  const [newSupplier, setNewSupplier] = useState({ name: '', contact_name: '', phone: '', category: '' });

  // Filtres
  const [filterProject, setFilterProject] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [filterStatus, setFilterStatus] = useState<PurchaseStatus | 'all'>('all');
  const [dateStart, setDateStart] = useState(getFirstDayOfMonth());
  const [dateEnd, setDateEnd] = useState(getToday());
  const [search, setSearch] = useState('');

  // Formulaire nouveau BL
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    supplier_id: '',
    bl_number: '',
    bl_date: getToday(),
    notes: ''
  });
  const [formLines, setFormLines] = useState<Partial<PurchaseLine>[]>([emptyLine()]);
  const [saving, setSaving] = useState(false);

  // PDF EXTRACTION
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [extractionResults, setExtractionResults] = useState<ExtractionResult[]>([]);
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [currentPdfFile, setCurrentPdfFile] = useState<File | null>(null);
  const [catalogFeedResult, setCatalogFeedResult] = useState<CatalogFeedResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Détail d'un BL ouvert
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedLines, setExpandedLines] = useState<PurchaseLine[]>([]);

  // Edition
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState({ bl_number: '', notes: '', status: '' as PurchaseStatus });
  const [editLines, setEditLines] = useState<(PurchaseLine | Partial<PurchaseLine>)[]>([]);

  // DATA (useQuery)
  const { data: purchases, loading: loadingPurchases, refresh: refreshPurchases } = useQuery<Purchase>(
    () => supabase
      .from('purchases')
      .select('*, project:projects(id, code, name), supplier:suppliers(id, name), employee:employees(id, full_name)')
      .gte('bl_date', dateStart)
      .lte('bl_date', dateEnd)
      .order('bl_date', { ascending: false }) as any,
    [dateStart, dateEnd]
  );

  const { data: suppliers, loading: loadingSuppliers, refresh: refreshSuppliers } = useQuery<Supplier>(
    () => supabase.from('suppliers').select('id, name, contact_name, phone, category').order('name'),
    []
  );

  const { data: projects, loading: loadingProjects } = useQuery<Project>(
    () => supabase.from('projects').select('id, code, name, is_active').order('code'),
    []
  );

  const loading = loadingPurchases || loadingSuppliers || loadingProjects;

  const fetchData = async () => {
    await Promise.all([refreshPurchases(), refreshSuppliers()]);
  };

  // Charger les lignes quand on déplie un BL
  useEffect(() => {
    if (!expandedId) { setExpandedLines([]); return; }
    supabase
      .from('purchase_lines')
      .select('*')
      .eq('purchase_id', expandedId)
      .order('created_at')
      .then(({ data }) => { if (data) setExpandedLines(data); });
  }, [expandedId]);

  // FILTRAGE
  const filtered = useMemo(() => {
    return purchases.filter(p => {
      if (filterProject !== 'all' && p.project_id !== filterProject) return false;
      if (filterSupplier !== 'all' && p.supplier_id !== filterSupplier) return false;
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      if (search) {
        const s = search.toLowerCase();
        const matchBL = p.bl_number?.toLowerCase().includes(s);
        const matchProject = p.project?.code?.toLowerCase().includes(s) || p.project?.name?.toLowerCase().includes(s);
        const matchSupplier = p.supplier?.name?.toLowerCase().includes(s);
        const matchNotes = p.notes?.toLowerCase().includes(s);
        if (!matchBL && !matchProject && !matchSupplier && !matchNotes) return false;
      }
      return true;
    });
  }, [purchases, filterProject, filterSupplier, filterStatus, search]);

  // Totaux
  const totalHT = useMemo(() => filtered.reduce((sum, p) => sum + (p.total_ht || 0), 0), [filtered]);

  return {
    // tabs
    activeTab, setActiveTab,
    // supplier form
    newSupplier, setNewSupplier,
    // filters
    filterProject, setFilterProject,
    filterSupplier, setFilterSupplier,
    filterStatus, setFilterStatus,
    dateStart, setDateStart,
    dateEnd, setDateEnd,
    search, setSearch,
    // BL form
    showForm, setShowForm,
    formData, setFormData,
    formLines, setFormLines,
    saving, setSaving,
    // PDF
    pdfFiles, setPdfFiles,
    extracting, setExtracting,
    extractionResults, setExtractionResults,
    activeResultIndex, setActiveResultIndex,
    dragOver, setDragOver,
    currentPdfFile, setCurrentPdfFile,
    catalogFeedResult, setCatalogFeedResult,
    fileInputRef,
    // expanded / lines
    expandedId, setExpandedId,
    expandedLines, setExpandedLines,
    // edit
    editingId, setEditingId,
    editData, setEditData,
    editLines, setEditLines,
    // data
    purchases,
    suppliers,
    projects,
    loading,
    refreshPurchases,
    refreshSuppliers,
    fetchData,
    // computed
    filtered,
    totalHT,
  };
}
